package com.mencanhealth.admin.ui

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.attributes.*
import com.mencanhealth.admin.data.AdminAppointmentService
import com.mencanhealth.admin.model.*
import kotlinx.coroutines.launch

private val DAY_NAMES = listOf("Lunes", "Martes", "Miercoles", "Jueves", "Viernes", "Sabado", "Domingo")

@Composable
fun AvailabilityManagementPage() {
    val scope = rememberCoroutineScope()
    val service = remember { AdminAppointmentService() }
    var blocks by remember { mutableStateOf<List<AvailabilityBlock>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var showAddDialog by remember { mutableStateOf(false) }
    var successMessage by remember { mutableStateOf<String?>(null) }
    var confirmDeleteId by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        blocks = service.listAvailabilityBlocks()
        loading = false
    }

    // Delete confirmation dialog
    confirmDeleteId?.let { id ->
        Div({ classes("dialog-overlay"); onClick { confirmDeleteId = null } }) {
            Div({ classes("dialog-card"); onClick { it.stopPropagation() } }) {
                H3 { Text("Confirmar eliminacion") }
                P { Text("Se eliminara este bloque de horario permanentemente.") }
                Div({ classes("dialog-actions") }) {
                    Button({
                        classes("btn-secondary")
                        onClick { confirmDeleteId = null }
                    }) { Text("Cancelar") }
                    Button({
                        classes("btn-danger")
                        onClick {
                            scope.launch {
                                try {
                                    service.removeAvailabilityBlock(id)
                                    blocks = service.listAvailabilityBlocks()
                                    successMessage = "Horario eliminado"
                                } catch (e: Exception) {
                                    successMessage = "Error: ${e.message}"
                                }
                                confirmDeleteId = null
                            }
                        }
                    }) { Text("Eliminar") }
                }
            }
        }
    }

    // Add dialog
    if (showAddDialog) {
        AddAvailabilityDialog(
            onSave = { block ->
                scope.launch {
                    try {
                        service.addAvailabilityBlock(block)
                        blocks = service.listAvailabilityBlocks()
                        showAddDialog = false
                        successMessage = "Horario agregado"
                    } catch (e: Exception) {
                        successMessage = "Error: ${e.message}"
                    }
                }
            },
            onCancel = { showAddDialog = false }
        )
    }

    Div({ classes("page") }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                property("margin-bottom", "16px")
            }
        }) {
            H2({ style { property("margin", "0") } }) { Text("Gestion de Horarios") }
            Button({
                classes("btn-small")
                style {
                    backgroundColor(Color("#2e7d32"))
                    color(Color.white)
                    property("padding", "8px 16px")
                    property("font-size", "14px")
                    property("border", "none")
                    property("border-radius", "4px")
                    property("cursor", "pointer")
                }
                onClick { showAddDialog = true }
            }) { Text("+ Agregar Horario") }
        }

        successMessage?.let { msg ->
            Div({
                classes(if (msg.startsWith("Error")) "error-banner" else "success-text")
                style { property("margin-bottom", "16px") }
            }) { Text(msg) }
        }

        if (loading) {
            P { Text("Cargando horarios...") }
        } else {
            // Weekly overview - recurring blocks
            val recurringBlocks = blocks.filter { it.isRecurring }

            H3 { Text("Horarios Recurrentes (Semanal)") }

            if (recurringBlocks.isEmpty()) {
                Div({ classes("empty-state") }) { Text("No hay horarios recurrentes configurados") }
            } else {
                Div({
                    classes("availability-grid")
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "repeat(7, 1fr)")
                        property("gap", "8px")
                        property("margin-bottom", "32px")
                    }
                }) {
                    // 0=Monday..6=Sunday mapping: dayOfWeek 0..6
                    for (dayIndex in 0..6) {
                        Div({
                            classes("availability-day")
                            style {
                                property("border", "1px solid #333")
                                property("border-radius", "8px")
                                property("padding", "8px")
                                property("min-height", "120px")
                            }
                        }) {
                            Div({
                                style {
                                    property("font-weight", "bold")
                                    property("margin-bottom", "8px")
                                    property("text-align", "center")
                                    property("border-bottom", "1px solid #444")
                                    property("padding-bottom", "4px")
                                }
                            }) { Text(DAY_NAMES[dayIndex]) }

                            val dayBlocks = recurringBlocks.filter { it.dayOfWeek == dayIndex }
                            if (dayBlocks.isEmpty()) {
                                Div({
                                    style {
                                        property("font-size", "12px")
                                        property("color", "#888")
                                        property("text-align", "center")
                                        property("padding-top", "16px")
                                    }
                                }) { Text("Sin horario") }
                            } else {
                                dayBlocks.forEach { block ->
                                    AvailabilityBlockCard(
                                        block = block,
                                        onToggle = {
                                            scope.launch {
                                                service.toggleAvailabilityBlock(block.id, !block.active)
                                                blocks = service.listAvailabilityBlocks()
                                            }
                                        },
                                        onDelete = { confirmDeleteId = block.id }
                                    )
                                }
                            }
                        }
                    }
                }
            }

            // Specific dates section
            val specificBlocks = blocks.filter { !it.isRecurring }

            H3 { Text("Fechas Especificas") }

            if (specificBlocks.isEmpty()) {
                Div({ classes("empty-state") }) { Text("No hay horarios para fechas especificas") }
            } else {
                Table({ classes("data-table") }) {
                    Thead {
                        Tr {
                            Th { Text("Fecha") }
                            Th { Text("Horario") }
                            Th { Text("Duracion de cita") }
                            Th { Text("Estado") }
                            Th { Text("Acciones") }
                        }
                    }
                    Tbody {
                        specificBlocks.sortedBy { it.specificDate }.forEach { block ->
                            Tr({
                                if (!block.active) {
                                    style { property("opacity", "0.5") }
                                }
                            }) {
                                Td { Text(block.specificDate) }
                                Td { Text("${block.startTime} - ${block.endTime}") }
                                Td { Text("${block.slotDurationMinutes} min") }
                                Td {
                                    Span({
                                        classes("status-badge", if (block.active) "status-completed" else "status-pending")
                                    }) {
                                        Text(if (block.active) "Activo" else "Inactivo")
                                    }
                                }
                                Td {
                                    Button({
                                        classes("btn-small")
                                        style { property("margin-right", "4px") }
                                        onClick {
                                            scope.launch {
                                                service.toggleAvailabilityBlock(block.id, !block.active)
                                                blocks = service.listAvailabilityBlocks()
                                            }
                                        }
                                    }) { Text(if (block.active) "Desactivar" else "Activar") }
                                    Button({
                                        classes("btn-danger")
                                        onClick { confirmDeleteId = block.id }
                                    }) { Text("Eliminar") }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun AvailabilityBlockCard(
    block: AvailabilityBlock,
    onToggle: () -> Unit,
    onDelete: () -> Unit
) {
    Div({
        classes("availability-block")
        style {
            property("background", if (block.active) "#1b5e20" else "#424242")
            property("border-radius", "6px")
            property("padding", "6px 8px")
            property("margin-bottom", "4px")
            property("font-size", "13px")
            if (!block.active) property("opacity", "0.6")
        }
    }) {
        Div({
            style { property("font-weight", "bold") }
        }) { Text("${block.startTime} - ${block.endTime}") }
        Div({
            style {
                property("font-size", "11px")
                property("color", "#ccc")
            }
        }) { Text("${block.slotDurationMinutes} min/cita") }
        Div({
            style {
                display(DisplayStyle.Flex)
                property("gap", "4px")
                property("margin-top", "4px")
            }
        }) {
            Button({
                style {
                    property("font-size", "11px")
                    property("padding", "2px 6px")
                    property("border", "none")
                    property("border-radius", "3px")
                    property("cursor", "pointer")
                    backgroundColor(if (block.active) Color("#ff8f00") else Color("#2e7d32"))
                    color(Color.white)
                }
                onClick { onToggle() }
            }) { Text(if (block.active) "Pausar" else "Activar") }
            Button({
                style {
                    property("font-size", "11px")
                    property("padding", "2px 6px")
                    property("border", "none")
                    property("border-radius", "3px")
                    property("cursor", "pointer")
                    backgroundColor(Color("#c62828"))
                    color(Color.white)
                }
                onClick { onDelete() }
            }) { Text("X") }
        }
    }
}

@Composable
private fun AddAvailabilityDialog(
    onSave: (AvailabilityBlock) -> Unit,
    onCancel: () -> Unit
) {
    var isRecurring by remember { mutableStateOf(true) }
    var selectedDay by remember { mutableStateOf(0) }
    var selectedDate by remember { mutableStateOf("") }
    var startTime by remember { mutableStateOf("09:00") }
    var endTime by remember { mutableStateOf("13:00") }
    var slotDuration by remember { mutableStateOf(30) }

    Div({ classes("dialog-overlay"); onClick { onCancel() } }) {
        Div({ classes("dialog-card"); onClick { it.stopPropagation() } }) {
            H3 { Text("Agregar Horario") }

            // Type selection
            Div({ style { property("margin-bottom", "12px") } }) {
                Label {
                    Input(InputType.Radio) {
                        attr("name", "blockType")
                        checked(isRecurring)
                        onChange { isRecurring = true }
                    }
                    Text(" Recurrente (semanal)")
                }
                Br()
                Label {
                    Input(InputType.Radio) {
                        attr("name", "blockType")
                        checked(!isRecurring)
                        onChange { isRecurring = false }
                    }
                    Text(" Fecha especifica")
                }
            }

            // Day or date selector
            if (isRecurring) {
                Div({ style { property("margin-bottom", "12px") } }) {
                    Label { Text("Dia de la semana") }
                    Br()
                    Select({
                        onChange { selectedDay = it.value?.toIntOrNull() ?: 0 }
                        style { property("padding", "6px"); property("width", "100%") }
                    }) {
                        DAY_NAMES.forEachIndexed { index, name ->
                            Option(index.toString(), { if (selectedDay == index) selected() }) { Text(name) }
                        }
                    }
                }
            } else {
                Div({ style { property("margin-bottom", "12px") } }) {
                    Label { Text("Fecha") }
                    Br()
                    Input(InputType.Date) {
                        value(selectedDate)
                        onInput { selectedDate = it.value }
                        style { property("padding", "6px"); property("width", "100%") }
                    }
                }
            }

            // Time inputs
            Div({
                style {
                    display(DisplayStyle.Flex)
                    property("gap", "12px")
                    property("margin-bottom", "12px")
                }
            }) {
                Div({ style { property("flex", "1") } }) {
                    Label { Text("Hora inicio") }
                    Br()
                    Input(InputType.Time) {
                        value(startTime)
                        onInput { startTime = it.value }
                        style { property("padding", "6px"); property("width", "100%") }
                    }
                }
                Div({ style { property("flex", "1") } }) {
                    Label { Text("Hora fin") }
                    Br()
                    Input(InputType.Time) {
                        value(endTime)
                        onInput { endTime = it.value }
                        style { property("padding", "6px"); property("width", "100%") }
                    }
                }
            }

            // Slot duration
            Div({ style { property("margin-bottom", "16px") } }) {
                Label { Text("Duracion de cita (minutos)") }
                Br()
                Select({
                    onChange { slotDuration = it.value?.toIntOrNull() ?: 30 }
                    style { property("padding", "6px"); property("width", "100%") }
                }) {
                    listOf(20, 30, 45, 60).forEach { min ->
                        Option(min.toString(), { if (slotDuration == min) selected() }) { Text("$min minutos") }
                    }
                }
            }

            // Actions
            Div({ classes("dialog-actions") }) {
                Button({
                    classes("btn-secondary")
                    onClick { onCancel() }
                }) { Text("Cancelar") }
                Button({
                    classes("btn-small")
                    style {
                        backgroundColor(Color("#2e7d32"))
                        color(Color.white)
                        property("border", "none")
                        property("padding", "8px 16px")
                        property("border-radius", "4px")
                        property("cursor", "pointer")
                    }
                    onClick {
                        onSave(
                            AvailabilityBlock(
                                dayOfWeek = if (isRecurring) selectedDay else -1,
                                specificDate = if (!isRecurring) selectedDate else "",
                                startTime = startTime,
                                endTime = endTime,
                                slotDurationMinutes = slotDuration,
                                isRecurring = isRecurring,
                                active = true
                            )
                        )
                    }
                }) { Text("Guardar") }
            }
        }
    }
}
